/* The colour theme: what people choose (a preference) and what the page shows (a theme).
   index.html's boot script applies the same rules before the first paint; keep the two in step
   (src/app/bootLocale.test.ts runs that script against these cases). */

export const THEME_PREFERENCES = ["system", "light", "dark"] as const;
export type ThemePreference = (typeof THEME_PREFERENCES)[number];
export type Theme = "light" | "dark";

export const THEME_STORAGE_KEY = "pmtool.theme";
export const DEFAULT_THEME_PREFERENCE: ThemePreference = "system";
export const DARK_SCHEME_QUERY = "(prefers-color-scheme: dark)";

export function isThemePreference(value: unknown): value is ThemePreference {
  return typeof value === "string" && (THEME_PREFERENCES as readonly string[]).includes(value);
}

/** The theme to show: the person's choice, or the system's when they follow it. */
export function resolveTheme(preference: ThemePreference, systemPrefersDark: boolean): Theme {
  if (preference === "system") return systemPrefersDark ? "dark" : "light";
  return preference;
}

/** The stored preference; "system" when there is none, it is unknown, or storage is blocked. */
export function readThemePreference(storage: Pick<Storage, "getItem">): ThemePreference {
  try {
    const stored = storage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(stored) ? stored : DEFAULT_THEME_PREFERENCE;
  } catch {
    return DEFAULT_THEME_PREFERENCE;
  }
}

/** Remember the preference; private browsing can block storage, and the choice still applies. */
export function storeThemePreference(
  storage: Pick<Storage, "setItem">,
  preference: ThemePreference,
): void {
  try {
    storage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // The theme still changes for this visit.
  }
}

/** Whether the system asks for dark; false where the browser cannot tell. */
export function systemPrefersDark(): boolean {
  return typeof window.matchMedia === "function" && window.matchMedia(DARK_SCHEME_QUERY).matches;
}
